(function () {
  'use strict';

  if (!document.body || document.body.dataset.dwNativeCartEnabled !== 'true') {
    return;
  }

  var settings = window.theme && window.theme.settings ? window.theme.settings : {};
  var pricingSettings = window.dwMemberPricing || {};
  var pricingMode = pricingSettings.mode === 'open_sale' ? 'open_sale' : 'members';
  var isMembersMode = pricingMode === 'members';
  var pricingLabels = pricingSettings.labels || {};
  var recommendationLimit = Math.max(4, Math.min(20, parseInt(settings.nativeCartRecommendationLimit, 10) || 20));
  var emptyRecommendationLimit = 3;
  var recommendationCache = {};
  var productCache = {};
  var productMap = {};
  var impressionKeys = {};
  var refreshTimer = null;
  var refreshToken = 0;
  var lastSignature = '';
  var lastCart = null;
  var lastCartHydratedAt = 0;
  var drawerHasOpened = false;
  var pendingProductSubmit = false;
  var pendingCartCount = null;
  var pageScrollLock = null;
  var debugState = window.__dwNativeCartDebug = window.__dwNativeCartDebug || {};

  function hasClearanceTag(product) {
    var tags = product && product.tags;

    if (!Array.isArray(tags)) tags = String(tags || '').split(',');

    return tags.some(function (tag) {
      var normalizedTag = String(tag || '').trim().toLowerCase();
      return normalizedTag === 'clearance' || normalizedTag === 'discontinued';
    });
  }

  function activePriceLabel(longLabel, product) {
    if (product && (product.isClearance || hasClearanceTag(product))) {
      return longLabel
        ? (pricingLabels.clearancePrice || 'CLEARANCE')
        : (pricingLabels.clearance || 'CLEARANCE');
    }

    if (pricingMode === 'open_sale') {
      return longLabel ? (pricingLabels.salePrice || 'SALE PRICE') : (pricingLabels.sale || 'SALE');
    }

    return longLabel ? (pricingLabels.memberPrice || 'MEMBERS PRICE') : (pricingLabels.member || 'MEMBERS');
  }

  function markCartStage(stage) {
    var cartPerformance = window.__dwCartPerformance;
    if (!cartPerformance || !cartPerformance.startedAt) return;

    var now = window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
    var elapsed = now - cartPerformance.startedAt;
    cartPerformance.stages = cartPerformance.stages || {};
    cartPerformance.stages[stage] = elapsed;
    debugState.cartPerformance = cartPerformance;

    if (!window.performance || typeof window.performance.mark !== 'function') return;

    var markName = 'dw-cart-' + stage;
    performance.mark(markName);

    if (stage === 'drawer-visible') {
      performance.measure('dw-cart-feedback', 'dw-cart-submit', markName);
    } else if (stage === 'recommendations-ready') {
      performance.measure('dw-cart-recommendations', 'dw-cart-submit', markName);
    }
  }

  function storefrontRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = root || '/';
    return root.slice(-1) === '/' ? root : root + '/';
  }

  function fetchJson(url, options) {
    var requestOptions = Object.assign({ credentials: 'same-origin' }, options || {});
    var timeout = null;

    if (!requestOptions.signal && window.AbortController) {
      var controller = new AbortController();
      requestOptions.signal = controller.signal;
      timeout = window.setTimeout(function () { controller.abort(); }, 10000);
    }

    return fetch(url, requestOptions).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok) {
          var error = new Error(payload.description || payload.message || 'Request failed');
          error.payload = payload;
          throw error;
        }
        return payload;
      });
    }).finally(function () {
      if (timeout) window.clearTimeout(timeout);
    });
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, settings.moneyFormat);
    }

    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'NZD'
    }).format((Number(cents) || 0) / 100);
  }

  function priceCents(value) {
    if (value === null || typeof value === 'undefined' || value === '') return 0;
    var stringValue = String(value).trim();
    var numericValue = Number(stringValue) || 0;

    // Collection JSON uses decimal currency while Ajax product and
    // recommendation payloads use integer minor units.
    return stringValue.indexOf('.') !== -1 ? Math.round(numericValue * 100) : Math.round(numericValue);
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function productType(product) {
    return normalize(product.product_type || product.type);
  }

  function availableVariants(product) {
    return (product.variants || []).filter(function (variant) {
      return variant.available !== false;
    });
  }

  function imageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function sizedImageUrl(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function productImageUrl(product) {
    var variant = availableVariants(product)[0];
    return imageUrl(
      variant && variant.featured_image ||
      product.featured_image ||
      (product.images || [])[0]
    );
  }

  function productUrl(product, variant) {
    var url = product.url || storefrontRoot() + 'products/' + product.handle;
    if (variant && variant.id && url.indexOf('variant=') === -1) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variant.id;
    }
    return url;
  }

  function isAccessoryType(value) {
    var words = ' ' + normalize(value).replace(/[^a-z0-9]+/g, ' ') + ' ';
    return /\s(accessories|accessory|repair|care|liner|liners|footprint|footprints|peg|pegs|pole|poles|pump|pumps|battery|batteries|filter|filters|cover|covers|case|cases|strap|straps|sock|socks|glove|gloves|hat|hats|beanie|beanies|mat|mats|pillow|pillows|cookware|cooking|fuel|meal|meals|bottle|bottles|hydration|first aid|kit|kits|headlamp|headlamps|torch|torches|lantern|lanterns|chair|chairs|groundsheet|groundsheets|awning|awnings)\s/.test(words);
  }

  function modelTokens(title, vendor) {
    var ignored = {
      alpine: true, black: true, blackout: true, blue: true, boot: true, boots: true,
      coat: true, down: true, dwights: true, fleece: true, green: true, hiking: true,
      hooded: true, jacket: true, jackets: true, light: true, mens: true, model: true,
      navy: true, outdoor: true, outdoors: true, pack: true, pants: true, peak: true,
      shirt: true, shoes: true, sleeping: true, softshell: true, tent: true, tents: true,
      top: true, ultralight: true, vest: true, waterproof: true, womens: true
    };
    var vendorTokens = normalize(vendor).split(/[^a-z0-9]+/).filter(Boolean);
    vendorTokens.forEach(function (token) { ignored[token] = true; });

    return normalize(title).split(/[^a-z0-9]+/).filter(function (token) {
      return token.length >= 5 && !ignored[token] && !/^\d+$/.test(token);
    });
  }

  function isLikelySubstitute(product, sourceItems) {
    var descriptor = [productType(product), product.title].join(' ');
    if (isAccessoryType(descriptor)) return false;

    var candidateTokens = modelTokens(product.title, product.vendor);
    if (!candidateTokens.length) return false;

    return (sourceItems || []).some(function (source) {
      var sourceTokens = modelTokens(source.product_title || source.title, source.vendor);
      return sourceTokens.some(function (token) {
        return candidateTokens.indexOf(token) !== -1;
      });
    });
  }

  function hasGenderMismatch(product, sourceItems) {
    var candidateTitle = ' ' + normalize(product.title) + ' ';
    var sourceTitles = (sourceItems || []).map(function (source) {
      return ' ' + normalize(source.product_title || source.title) + ' ';
    });
    var sourcesAreMens = sourceTitles.length && sourceTitles.every(function (title) {
      return /\s(mens|men's|male)\s/.test(title) && !/\s(womens|women's|female)\s/.test(title);
    });
    var sourcesAreWomens = sourceTitles.length && sourceTitles.every(function (title) {
      return /\s(womens|women's|female)\s/.test(title) && !/\s(mens|men's|male)\s/.test(title);
    });

    if (sourcesAreMens && /\s(womens|women's|female)\s/.test(candidateTitle)) return true;
    if (sourcesAreWomens && /\s(mens|men's|male)\s/.test(candidateTitle)) return true;
    return false;
  }

  function productFamily(value) {
    var words = ' ' + normalize(value).replace(/[^a-z0-9]+/g, ' ') + ' ';
    if (/\s(jacket|jackets|coat|coats|vest|vests|fleece|fleeces|shirt|shirts|top|tops|pants|trousers|base layer|thermal|glove|gloves|mittens|beanie|beanies|hat|hats|sock|socks)\s/.test(words)) return 'apparel';
    if (/\s(tent|tents|shelter|shelters|gazebo|gazebos|awning|awnings|groundsheet|groundsheets|footprint|footprints|peg|pegs|pole|poles)\s/.test(words)) return 'shelter';
    if (/\s(sleeping bag|sleeping bags|sleep mat|sleeping mat|sleeping mats|mattress|mattresses|quilt|quilts|pillow|pillows|liner|liners|pump|pumps)\s/.test(words)) return 'sleep';
    if (/\s(boot|boots|shoe|shoes|footwear|sandal|sandals|gaiter|gaiters|insole|insoles|laces)\s/.test(words)) return 'footwear';
    if (/\s(pack|packs|backpack|backpacks|rucksack|rucksacks|luggage|duffel|duffels|hydration|dry bag|dry bags)\s/.test(words)) return 'packs';
    if (/\s(headlamp|headlamps|head torch|head torches|torch|torches|flashlight|flashlights|lantern|lanterns)\s/.test(words)) return 'lighting';
    if (/\s(stove|stoves|cookware|cooking|fuel|meal|meals|kitchen|utensil|utensils)\s/.test(words)) return 'cooking';
    return '';
  }

  function hasCategoryMismatch(product, sourceItems) {
    var sourceFamilies = (sourceItems || []).map(function (source) {
      return productFamily([source.product_type, source.product_title || source.title].join(' '));
    }).filter(Boolean);
    var uniqueFamilies = sourceFamilies.filter(function (family, index) {
      return sourceFamilies.indexOf(family) === index;
    });
    if (uniqueFamilies.length !== 1) return false;

    var sourceFamily = uniqueFamilies[0];
    var candidateFamily = productFamily([productType(product), product.title].join(' '));
    if (!candidateFamily) return false;

    if (sourceFamily === 'apparel') return candidateFamily !== 'apparel' && candidateFamily !== 'footwear';
    if (sourceFamily === 'footwear') return candidateFamily !== 'footwear' && candidateFamily !== 'apparel';
    if (sourceFamily === 'shelter') return candidateFamily === 'apparel' || candidateFamily === 'footwear' || candidateFamily === 'cooking';
    if (sourceFamily === 'sleep') return candidateFamily === 'apparel' || candidateFamily === 'footwear' || candidateFamily === 'cooking';
    if (sourceFamily === 'packs') return candidateFamily === 'shelter' || candidateFamily === 'sleep' || candidateFamily === 'cooking';
    if (sourceFamily === 'lighting') return candidateFamily === 'apparel' || candidateFamily === 'footwear';
    if (sourceFamily === 'cooking') return candidateFamily === 'apparel' || candidateFamily === 'footwear';
    return false;
  }

  function isEligible(product, inCartIds, cartTypes, intent, sourceItems) {
    if (!product || !product.id || !product.handle || product.available === false) return false;
    if (!availableVariants(product).length) return false;
    if (!productImageUrl(product)) return false;
    if (inCartIds[String(product.id)]) return false;
    if (/gift card/.test(normalize(product.title))) return false;
    if (hasGenderMismatch(product, sourceItems)) return false;

    var type = productType(product);
    var descriptor = [type, product.title].join(' ');
    if ((intent === 'related' || intent === 'bridge') && type && cartTypes[type] && !isAccessoryType(descriptor)) {
      return false;
    }
    if ((intent === 'related' || intent === 'bridge') && isLikelySubstitute(product, sourceItems)) return false;
    if (intent === 'bridge' && hasCategoryMismatch(product, sourceItems)) return false;

    return true;
  }

  function recommendationUrl(productId, intent) {
    return storefrontRoot() + 'recommendations/products.json?product_id=' +
      encodeURIComponent(productId) + '&limit=10&intent=' + encodeURIComponent(intent);
  }

  function fetchRecommendations(productId, intent) {
    var key = String(productId) + ':' + intent;
    if (recommendationCache[key]) {
      return recommendationCache[key];
    }

    recommendationCache[key] = fetchJson(recommendationUrl(productId, intent), {
      credentials: 'same-origin'
    }).then(function (payload) {
      return payload.products || [];
    }).catch(function () {
      return [];
    });

    return recommendationCache[key];
  }

  function addCandidate(candidates, product, score, sourceId, intent) {
    var key = String(product.id);
    var candidate = candidates[key];

    if (!candidate) {
      candidate = {
        product: product,
        score: 0,
        sources: {},
        complementary: false
      };
      candidates[key] = candidate;
    }

    if (!candidate.sources[sourceId]) {
      candidate.sources[sourceId] = true;
      candidate.score += Object.keys(candidate.sources).length > 1 ? 24 : 0;
    }

    candidate.score += score;
    if (intent === 'complementary') {
      candidate.complementary = true;
    }
  }

  function sortedCandidates(candidates) {
    return Object.keys(candidates).map(function (key) {
      return candidates[key];
    }).sort(function (a, b) {
      if (a.complementary !== b.complementary) return a.complementary ? -1 : 1;
      return b.score - a.score;
    }).map(function (candidate) {
      return candidate.product;
    });
  }

  function isTentOrShelterItem(item) {
    var type = normalize(item.product_type);
    var title = normalize(item.product_title || item.title);
    return (/tent|shelter|gazebo|bivi/.test(type) || /emergency.*shelter/.test(title)) &&
      !/accessor|footprint|pole|part|connector|carry|tube|wall/.test(type) &&
      !/replacement|roof cover|footprint only|solid wall kit|side wall|shelter sunwall|floor insert|spare groundsheet|canopy only|\bpump\b|front porch|removable rear veranda/.test(title);
  }

  function cartRecommendationProducts(cart, onInitialProducts) {
    var inCartIds = {};
    var cartTypes = {};
    var sourceItems = [];

    (cart.items || []).forEach(function (item) {
      inCartIds[String(item.product_id)] = true;
      var type = normalize(item.product_type);
      if (type) cartTypes[type] = true;
      if (!sourceItems.some(function (source) { return String(source.product_id) === String(item.product_id); })) {
        sourceItems.push(item);
      }
    });

    var tentSources = sourceItems.filter(isTentOrShelterItem);
    sourceItems = sourceItems.slice(0, 6);
    var candidates = {};
    var lastProgressIds = '';

    function emitProgress() {
      var products = sortedCandidates(candidates).slice(0, recommendationLimit);
      var productIds = products.map(function (product) { return product.id; }).join(',');

      if (productIds && productIds !== lastProgressIds && typeof onInitialProducts === 'function') {
        lastProgressIds = productIds;
        onInitialProducts(products);
      }

      return products;
    }

    if (tentSources.length) {
      return Promise.all(tentSources.map(function (item) {
        return fetchRecommendations(item.product_id, 'complementary').then(function (products) {
          products.forEach(function (product, index) {
            if (isEligible(product, inCartIds, cartTypes, 'complementary', tentSources)) {
              addCandidate(candidates, product, 180 - index * 5, String(item.product_id), 'complementary');
            }
          });
        });
      })).then(emitProgress);
    }

    return Promise.all(sourceItems.map(function (item) {
      return fetchRecommendations(item.product_id, 'complementary').then(function (products) {
        products.forEach(function (product, index) {
          if (isEligible(product, inCartIds, cartTypes, 'complementary', sourceItems)) {
            addCandidate(candidates, product, 180 - index * 5, String(item.product_id), 'complementary');
          }
        });
      });
    })).then(function () {
      var complementaryProducts = emitProgress();
      if (Object.keys(candidates).length >= recommendationLimit) {
        return complementaryProducts;
      }

      return Promise.all(sourceItems.map(function (item) {
        return fetchRecommendations(item.product_id, 'related').then(function (products) {
          products.forEach(function (product, index) {
            if (isEligible(product, inCartIds, cartTypes, 'related', sourceItems)) {
              addCandidate(candidates, product, 55 - index * 2, String(item.product_id), 'related');
            }
          });
        });
      })).then(function () {
        var initialProducts = emitProgress();
        if (initialProducts.length >= recommendationLimit) {
          return initialProducts;
        }

        // A second hop through the strongest first-party recommendations finds
        // adjacent items (for example gloves or base layers for a jacket)
        // without padding the carousel with direct substitutes.
        var bridgeSeeds = initialProducts.slice(0, 4);
        return Promise.all(bridgeSeeds.map(function (seed) {
          return fetchRecommendations(seed.id, 'related').then(function (products) {
            products.forEach(function (product, index) {
              if (isEligible(product, inCartIds, cartTypes, 'bridge', sourceItems)) {
                var descriptor = [productType(product), product.title].join(' ');
                var accessoryBonus = isAccessoryType(descriptor) ? 36 : 0;
                addCandidate(candidates, product, 32 - index + accessoryBonus, 'bridge:' + seed.id, 'related');
              }
            });
          });
        })).then(function () {
          return sortedCandidates(candidates).slice(0, recommendationLimit);
        });
      });
    });
  }

  function recentHandles() {
    try {
      var recent = JSON.parse(window.localStorage.getItem('theme-recent') || '{}');
      return Object.keys(recent).filter(Boolean).reverse().slice(0, 8);
    } catch (error) {
      return [];
    }
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetchJson(storefrontRoot() + 'products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    }).catch(function () {
      return null;
    });

    return productCache[handle];
  }

  function fetchAutomaticFallback() {
    return fetchJson(storefrontRoot() + 'collections/popular-this-month/products.json?limit=12', {
      credentials: 'same-origin'
    }).then(function (payload) {
      return payload.products || [];
    }).catch(function () {
      return [];
    });
  }

  function emptyRecommendationProducts() {
    var handles = recentHandles();
    var inCartIds = {};
    var cartTypes = {};
    var currentProductId = document.body.dataset.dwCurrentProductId;

    return Promise.all(handles.map(fetchProduct)).then(function (recentProducts) {
      var products = recentProducts.filter(function (product) {
        return isEligible(product, inCartIds, cartTypes, 'recent');
      });

      if (products.length >= emptyRecommendationLimit) {
        return products.slice(0, emptyRecommendationLimit);
      }

      var currentProductPromise = currentProductId ?
        fetchRecommendations(currentProductId, 'complementary') : Promise.resolve([]);

      return currentProductPromise.then(function (currentRecommendations) {
        var seen = {};
        products.forEach(function (product) { seen[String(product.id)] = true; });
        if (currentProductId) seen[String(currentProductId)] = true;

        currentRecommendations.some(function (product) {
          if (products.length >= emptyRecommendationLimit) return true;
          if (!seen[String(product.id)] && isEligible(product, inCartIds, cartTypes, 'recent')) {
            seen[String(product.id)] = true;
            products.push(product);
          }
          return false;
        });

        if (products.length >= emptyRecommendationLimit) {
          return products.slice(0, emptyRecommendationLimit);
        }

        var seed = products[0];
        var fillPromise = seed ? fetchRecommendations(seed.id, 'related') : fetchAutomaticFallback();

        return fillPromise.then(function (fallbacks) {
          fallbacks.some(function (product) {
            if (products.length >= emptyRecommendationLimit) return true;
            if (!seen[String(product.id)] && isEligible(product, inCartIds, cartTypes, 'recent')) {
              seen[String(product.id)] = true;
              products.push(product);
            }
            return false;
          });

          if (products.length >= emptyRecommendationLimit) {
            return products.slice(0, emptyRecommendationLimit);
          }

          return fetchAutomaticFallback().then(function (curatedProducts) {
            curatedProducts.some(function (product) {
              if (products.length >= emptyRecommendationLimit) return true;
              if (!seen[String(product.id)] && isEligible(product, inCartIds, cartTypes, 'recent')) {
                seen[String(product.id)] = true;
                products.push(product);
              }
              return false;
            });

            return products.slice(0, emptyRecommendationLimit);
          });
        });
      });
    });
  }

  function optionName(product, index) {
    var option = (product.options || [])[index];
    return normalize(typeof option === 'string' ? option : option && option.name);
  }

  function preferredVariant(product, cart) {
    var variants = availableVariants(product);
    if (!variants.length) return null;

    var cartColours = {};
    (cart && cart.items || []).forEach(function (item) {
      (item.options_with_values || []).forEach(function (option) {
        if (/colou?r/.test(normalize(option.name))) {
          cartColours[normalize(option.value)] = true;
        }
      });
    });

    var colourIndex = -1;
    (product.options || []).some(function (option, index) {
      if (/colou?r/.test(optionName(product, index))) {
        colourIndex = index;
        return true;
      }
      return false;
    });

    if (colourIndex > -1) {
      var matched = variants.find(function (variant) {
        return cartColours[normalize((variant.options || [])[colourIndex])];
      });
      if (matched) return matched;
    }

    return variants[0];
  }

  function createElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text !== 'undefined') element.textContent = text;
    return element;
  }

  function renderPrice(container, variant, product) {
    container.innerHTML = '';
    var price = priceCents(variant.price);
    var compare = priceCents(variant.compare_at_price);
    var discount = compare > price ? Math.round((compare - price) * 100 / compare) : 0;

    var rawDiscount = compare > price ? (compare - price) * 100 / compare : 0;
    var trial = pricingSettings.quietMemberPrices && isMembersMode;
    var quietPrice = trial && !hasClearanceTag(product) && rawDiscount > 0 && rawDiscount < 2;
    var promoted = rawDiscount >= 2;
    container.classList.toggle('dw-price-quiet', Boolean(quietPrice));
    if (promoted || hasClearanceTag(product) && compare > price) {
      container.appendChild(createElement('span', 'dw-native-cart-card__compare', formatMoney(compare)));
    }

    if (promoted || quietPrice || hasClearanceTag(product)) {
      var memberRow = createElement('div', 'dw-native-cart-card__member-row');
      memberRow.appendChild(createElement('span', 'dw-native-cart-card__member-label', quietPrice ? (pricingSettings.loggedIn ? pricingLabels.quietMemberActive : pricingLabels.quietMember) : activePriceLabel(false, product)));
      memberRow.appendChild(createElement('strong', 'dw-native-cart-card__member-price', formatMoney(price)));
      container.appendChild(memberRow);
      if (promoted) {
        container.appendChild(createElement('span', 'dw-native-cart-card__saving', discount + '% OFF'));
      }
    } else {
      container.appendChild(createElement('strong', 'dw-native-cart-card__price', formatMoney(price)));
    }
  }

  function variantLabel(variant) {
    return variant.title && variant.title !== 'Default Title' ? variant.title : 'Regular';
  }

  function buildProductCard(product, cart, context) {
    var variant = preferredVariant(product, cart);
    var initialImage = imageUrl(variant && variant.featured_image) || productImageUrl(product);
    if (!variant || !initialImage) return null;

    productMap[String(product.id)] = product;
    var card = createElement('article', 'dw-native-cart-card');
    card.dataset.productId = product.id;
    card.dataset.context = context;
    card.dataset.dwClearanceProduct = hasClearanceTag(product) ? 'true' : 'false';

    var imageLink = createElement('a', 'dw-native-cart-card__image-link');
    imageLink.href = productUrl(product, variant);
    var image = createElement('img', 'dw-native-cart-card__image');
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = product.title || '';
    image.src = sizedImageUrl(initialImage, 240);
    imageLink.appendChild(image);
    card.appendChild(imageLink);

    var content = createElement('div', 'dw-native-cart-card__content');
    var titleLink = createElement('a', 'dw-native-cart-card__title', product.title);
    titleLink.href = productUrl(product, variant);
    content.appendChild(titleLink);

    var price = createElement('div', 'dw-native-cart-card__pricing');
    renderPrice(price, variant, product);
    content.appendChild(price);

    var variants = availableVariants(product);
    if (variants.length > 1 || variant.title !== 'Default Title') {
      var select = createElement('select', 'dw-native-cart-card__variant');
      select.setAttribute('aria-label', 'Choose an option for ' + product.title);
      variants.forEach(function (optionVariant) {
        var option = createElement('option', '', variantLabel(optionVariant));
        option.value = optionVariant.id;
        option.selected = String(optionVariant.id) === String(variant.id);
        select.appendChild(option);
      });
      content.appendChild(select);
    }

    var actions = createElement('div', 'dw-native-cart-card__actions');
    var addButton = createElement('button', 'dw-native-cart-card__add', 'Add');
    addButton.type = 'button';
    addButton.dataset.variantId = variant.id;
    actions.appendChild(addButton);
    content.appendChild(actions);
    content.appendChild(createElement('div', 'dw-native-cart-card__status'));
    card.appendChild(content);

    return card;
  }

  function recommendationShell(container) {
    return container && container.closest('[data-dw-native-cart-recommendations-shell]');
  }

  function setRecommendationVisibility(container, visible) {
    if (!container) return;
    var shell = recommendationShell(container);

    container.hidden = !visible;
    if (!shell) return;

    shell.hidden = !visible;
    if (!visible) shell.open = false;
    if (visible) shell.open = true;
  }

  function setRecommendationCount(container, count) {
    var shell = recommendationShell(container);
    var action = shell && shell.querySelector('[data-dw-native-cart-recommendations-action]');

    if (!action) return;
    action.textContent = count ? 'View ' + count + (count === 1 ? ' item' : ' items') : 'Loading';
  }

  function setLoading(container, title) {
    if (!container) return;
    setRecommendationVisibility(container, true);
    setRecommendationCount(container, 0);
    container.innerHTML = '';
    if (!recommendationShell(container)) {
      container.appendChild(createElement('h3', 'dw-native-cart-recommendations__title', title));
    }
    var skeleton = createElement('div', 'dw-native-cart-skeleton');
    skeleton.innerHTML = '<span></span><div><span></span><span></span><span></span></div>';
    container.appendChild(skeleton);
  }

  function track(eventName, products, context, variant) {
    var productList = (Array.isArray(products) ? products : [products]).filter(Boolean).map(function (product) {
      return {
        product_id: product.id,
        variant_id: variant && variant.id,
        handle: product.handle,
        title: product.title,
        price: variant ? priceCents(variant.price) : priceCents(product.price)
      };
    });
    var payload = { context: context, products: productList };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, dw_native_cart: payload });
    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
  }

  function updateNavigation(container) {
    var trackElement = container.querySelector('[data-dw-native-cart-track]');
    var previous = container.querySelector('[data-dw-native-cart-prev]');
    var next = container.querySelector('[data-dw-native-cart-next]');
    if (!trackElement || !previous || !next) return;

    var maxScroll = Math.max(0, trackElement.scrollWidth - trackElement.clientWidth);
    previous.disabled = trackElement.scrollLeft < 4;
    next.disabled = trackElement.scrollLeft > maxScroll - 4;
  }

  function updateAllNavigation() {
    document.querySelectorAll('.dw-native-cart-recommendations').forEach(function (container) {
      updateNavigation(container);
    });
  }

  function renderRecommendations(container, title, products, cart, context, signature) {
    if (!container) return;
    if (container._dwNativeCartResizeObserver) {
      container._dwNativeCartResizeObserver.disconnect();
      container._dwNativeCartResizeObserver = null;
    }
    container.innerHTML = '';

    if (!products.length) {
      setRecommendationVisibility(container, false);
      return;
    }

    setRecommendationVisibility(container, true);
    var header = createElement('div', 'dw-native-cart-recommendations__header');
    if (!recommendationShell(container)) {
      header.appendChild(createElement('h3', 'dw-native-cart-recommendations__title', title));
    } else {
      header.classList.add('dw-native-cart-recommendations__header--after-checkout');
    }

    var navigation = createElement('div', 'dw-native-cart-recommendations__nav');
    var previous = createElement('button', 'dw-native-cart-recommendations__arrow', '<');
    previous.type = 'button';
    previous.dataset.dwNativeCartPrev = '';
    previous.setAttribute('aria-label', 'Previous recommendation');
    var next = createElement('button', 'dw-native-cart-recommendations__arrow', '>');
    next.type = 'button';
    next.dataset.dwNativeCartNext = '';
    next.setAttribute('aria-label', 'Next recommendation');
    navigation.appendChild(previous);
    navigation.appendChild(next);
    if (products.length < 2) navigation.hidden = true;
    header.appendChild(navigation);
    container.appendChild(header);

    var trackElement = createElement('div', 'dw-native-cart-recommendations__track');
    trackElement.dataset.dwNativeCartTrack = '';
    products.forEach(function (product) {
      var card = buildProductCard(product, cart, context);
      if (card) trackElement.appendChild(card);
    });
    if (!trackElement.children.length) {
      setRecommendationVisibility(container, false);
      return;
    }
    container.appendChild(trackElement);
    setRecommendationCount(container, trackElement.children.length);

    trackElement.addEventListener('scroll', function () {
      updateNavigation(container);
    }, { passive: true });
    if (window.ResizeObserver) {
      container._dwNativeCartResizeObserver = new ResizeObserver(function () {
        updateNavigation(container);
      });
      container._dwNativeCartResizeObserver.observe(trackElement);
    }
    window.requestAnimationFrame(function () { updateNavigation(container); });

    var impressionKey = context + ':' + signature + ':' + products.map(function (product) { return product.id; }).join(',');
    if (!impressionKeys[impressionKey]) {
      impressionKeys[impressionKey] = true;
      track('dw_native_cart_recommendations_view', products, context);
    }
  }

  function signatureFor(cart) {
    return (cart.items || []).map(function (item) {
      return [item.product_id, item.variant_id, item.quantity].join(':');
    }).join('|') || 'empty';
  }

  function updateCount(count) {
    document.querySelectorAll('[data-dw-native-cart-count]').forEach(function (node) {
      node.textContent = count;
    });
  }

  function canLoadRecommendations(container) {
    if (!container) return false;
    var drawer = container.closest('#CartDrawer');
    if (!drawer) return true;
    if (drawer.classList.contains('drawer--is-open')) drawerHasOpened = true;
    return drawerHasOpened;
  }

  function renderCart(cart, fromCache) {
    if (!cart || !Array.isArray(cart.items)) return false;

    updateCount(cart.item_count || 0);
    lastCart = cart;
    if (!fromCache) lastCartHydratedAt = Date.now();

    var signature = signatureFor(cart);
    var cartContainer = document.querySelector('[data-dw-native-cart-recommendations]');
    var emptyContainer = document.querySelector('[data-dw-native-cart-empty-recommendations]');

    if (!canLoadRecommendations(cart.item_count > 0 ? cartContainer : emptyContainer)) return true;
    if (signature === lastSignature) return true;
    lastSignature = signature;
    var token = ++refreshToken;

    if (cart.item_count > 0) {
      if (emptyContainer) emptyContainer.hidden = true;
      setLoading(cartContainer, "You'll love these");
      debugState.status = 'loading-cart-recommendations';
      var renderedProductIds = '';
      cartRecommendationProducts(cart, function (initialProducts) {
        if (token !== refreshToken || !initialProducts.length) return;
        renderedProductIds = initialProducts.map(function (product) { return product.id; }).join(',');
        renderRecommendations(cartContainer, "You'll love these", initialProducts, cart, 'cart', signature);
        markCartStage('recommendations-partial');
        debugState.status = 'ready-partial';
        debugState.products = initialProducts.map(function (product) { return product.title; });
      }).then(function (products) {
        if (token !== refreshToken) return;
        var productIds = products.map(function (product) { return product.id; }).join(',');
        if (productIds !== renderedProductIds) {
          renderRecommendations(cartContainer, "You'll love these", products, cart, 'cart', signature);
        }
        markCartStage('recommendations-ready');
        debugState.status = 'ready';
        debugState.products = products.map(function (product) { return product.title; });
      }).catch(function (error) {
        if (token !== refreshToken) return;
        setRecommendationVisibility(cartContainer, false);
        debugState.status = 'recommendation-error';
        debugState.error = error && error.message ? error.message : String(error);
      });
    } else {
      setRecommendationVisibility(cartContainer, false);
      setLoading(emptyContainer, 'You may also like');
      debugState.status = 'loading-empty-recommendations';
      emptyRecommendationProducts().then(function (products) {
        if (token !== refreshToken) return;
        renderRecommendations(emptyContainer, 'You may also like', products, cart, 'empty-cart', signature);
        debugState.status = 'ready';
        debugState.products = products.map(function (product) { return product.title; });
      }).catch(function (error) {
        if (token !== refreshToken) return;
        if (emptyContainer) emptyContainer.hidden = true;
        debugState.status = 'recommendation-error';
        debugState.error = error && error.message ? error.message : String(error);
      });
    }

    return true;
  }

  function awaitingCartMarkup() {
    var form = document.querySelector('#CartDrawerForm');
    var hydration = form && form.dataset.dwCartHydration;
    return hydration === 'pending' || hydration === 'loading';
  }

  function refresh() {
    if (awaitingCartMarkup()) return;
    debugState.status = 'loading-cart';
    debugState.error = '';
    fetchJson(storefrontRoot() + 'cart.js?t=' + Date.now(), { credentials: 'same-origin' }).then(function (cart) {
      renderCart(cart);
    }).catch(function (error) {
      var cartContainer = document.querySelector('[data-dw-native-cart-recommendations]');
      var emptyContainer = document.querySelector('[data-dw-native-cart-empty-recommendations]');
      setRecommendationVisibility(cartContainer, false);
      if (emptyContainer) emptyContainer.hidden = true;
      debugState.status = 'cart-error';
      debugState.error = error && error.message ? error.message : String(error);
    });
  }

  function scheduleRefresh(delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, typeof delay === 'number' ? delay : 40);
  }

  function selectedVariant(card, product) {
    var select = card.querySelector('.dw-native-cart-card__variant');
    var variantId = select ? select.value : card.querySelector('.dw-native-cart-card__add').dataset.variantId;
    return (product.variants || []).find(function (variant) {
      return String(variant.id) === String(variantId);
    });
  }

  function updateCardVariant(card) {
    var product = productMap[String(card.dataset.productId)];
    if (!product) return;
    var variant = selectedVariant(card, product);
    if (!variant) return;

    var image = card.querySelector('.dw-native-cart-card__image');
    var link = card.querySelector('.dw-native-cart-card__image-link');
    var titleLink = card.querySelector('.dw-native-cart-card__title');
    var button = card.querySelector('.dw-native-cart-card__add');
    var url = productUrl(product, variant);
    var variantImage = imageUrl(variant.featured_image || product.featured_image || (product.images || [])[0]);

    if (variantImage) image.src = sizedImageUrl(variantImage, 240);
    link.href = url;
    titleLink.href = url;
    button.dataset.variantId = variant.id;
    renderPrice(card.querySelector('.dw-native-cart-card__pricing'), variant, product);
  }

  function setCardStatus(card, message, isError) {
    var status = card.querySelector('.dw-native-cart-card__status');
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function addRecommendation(button) {
    var card = button.closest('.dw-native-cart-card');
    var product = productMap[String(card.dataset.productId)];
    var variant = selectedVariant(card, product);
    if (!product || !variant) return;

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';
    setCardStatus(card, '', false);
    setUpdating(true);

    fetchJson(window.theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: Number(variant.id), quantity: 1 }],
        sections: ['dw-native-cart-render'],
        sections_url: window.location.pathname
      })
    }).then(function (addResult) {
      track('dw_native_cart_recommendation_add', product, card.dataset.context, variant);
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: product,
          variant: variant,
          source: 'dw-native-cart',
          sections: addResult.sections || {}
        }
      }));
    }).catch(function (error) {
      setUpdating(false);
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = 'Add';
      setCardStatus(card, error.message || 'This option could not be added.', true);
    });
  }

  function removeCartItem(link) {
    var item = link.closest('.cart__item');
    if (!item || !item.dataset.key || !window.theme || !theme.cart) return;

    item.classList.add('is-loading');
    setUpdating(true);
    theme.cart.changeItem(item.dataset.key, 0).then(function (cartText) {
      var cart = JSON.parse(cartText);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      var nativeCartMarkup = cart.sections && cart.sections['dw-native-cart-render'];
      if (nativeCartMarkup) {
        document.dispatchEvent(new CustomEvent('cart:render-markup', {
          detail: { markup: nativeCartMarkup }
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:build'));
      }
    }).catch(function () {
      setUpdating(false);
      item.classList.remove('is-loading');
    });
  }

  function setUpdating(isUpdating) {
    var drawer = document.querySelector('#CartDrawer');
    if (drawer) drawer.classList.toggle('dw-native-cart--updating', Boolean(isUpdating));
  }

  function selectedProductData(form) {
    var section = form.closest('[data-section-type="product"]');
    var variantIdInput = form.querySelector('[name="id"]');
    var variantId = variantIdInput && variantIdInput.value;
    var variant = null;

    if (section) {
      var variantsJson = section.querySelector('[data-variant-json]');
      if (variantsJson) {
        try {
          variant = JSON.parse(variantsJson.textContent || '[]').find(function (candidate) {
            return String(candidate.id) === String(variantId);
          }) || null;
        } catch (error) {
          variant = null;
        }
      }
    }

    var image = variant && variant.featured_image && variant.featured_image.src;
    if (!image && section) {
      var activeImage = section.querySelector('[data-product-image-main] img');
      image = activeImage && (activeImage.currentSrc || activeImage.src);
    }

    var title = section && section.dataset.productTitle;
    if (!title) {
      var titleElement = document.querySelector('.product-single__title');
      title = titleElement && titleElement.textContent.trim();
    }

    var url = section && section.dataset.productUrl || window.location.pathname;
    if (variantId) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(variantId);
    }

    var variantTitle = variant && (variant.public_title || variant.title) || '';
    if (normalize(variantTitle) === 'default title') variantTitle = '';

    return {
      title: title || 'Selected item',
      url: url,
      image: image ? sizedImageUrl(image, 180) : '',
      variantTitle: variantTitle,
      price: Number(variant && variant.price) || 0,
      compareAtPrice: Number(variant && variant.compare_at_price) || 0,
      isClearance: Boolean(
        section && section.getAttribute('data-dw-clearance-product') === 'true' ||
        pricingSettings.isClearanceProduct
      )
    };
  }

  function appendPendingPrice(container, product) {
    var compare = product.compareAtPrice;
    var price = product.price;
    var discount = compare > price ? Math.round((compare - price) * 100 / compare) : 0;
    var isMemberPrice = compare > price && (compare - price) * 100 >= compare * 2;
    var loggedIn = Boolean(pricingSettings.loggedIn);
    var isClearance = Boolean(product.isClearance);
    var rawDiscount = compare > price ? (compare - price) * 100 / compare : 0;
    var quietPrice = pricingSettings.quietMemberPrices && isMembersMode && !isClearance && rawDiscount > 0 && rawDiscount < 2;
    container.classList.toggle('dw-price-quiet', Boolean(quietPrice));

    if (isMemberPrice && isMembersMode && !loggedIn && !isClearance) {
      container.appendChild(createElement('strong', 'dw-native-cart-pending__price', formatMoney(compare)));
      return;
    }

    if (isMemberPrice || isClearance && compare > price) {
      container.appendChild(createElement('span', 'dw-native-cart-pending__compare', formatMoney(compare)));
    }

    if (quietPrice || isClearance || isMemberPrice && (!isMembersMode || loggedIn)) {
      container.appendChild(createElement('span', 'dw-native-cart-pending__member-label', quietPrice ? (loggedIn ? pricingLabels.quietMemberActive : pricingLabels.quietMember) : activePriceLabel(false, product)));
    }

    container.appendChild(createElement('strong', isMemberPrice || isClearance ? 'dw-native-cart-pending__price dw-native-cart-pending__price--member' : 'dw-native-cart-pending__price', formatMoney(price || compare)));
  }

  function clearPendingAdd(restoreCount) {
    var drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    drawer.querySelectorAll('[data-dw-native-cart-pending]').forEach(function (node) {
      node.remove();
    });
    drawer.classList.remove('dw-native-cart--adding');

    if (restoreCount && pendingCartCount !== null) {
      updateCount(pendingCartCount);
      if (pendingCartCount === 0) drawer.classList.add('is-empty');
    }

    pendingCartCount = null;
  }

  function showPendingAdd(form) {
    var drawer = document.querySelector('#CartDrawer');
    var products = drawer && drawer.querySelector('[data-products]');
    if (!drawer || !products) return;

    clearPendingAdd(false);

    var countNode = drawer.querySelector('[data-dw-native-cart-count]');
    var quantityInput = form.querySelector('[name="quantity"]');
    var quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
    var product = selectedProductData(form);
    pendingCartCount = Math.max(0, parseInt(countNode && countNode.textContent, 10) || 0);

    var pending = createElement('div', 'dw-native-cart-pending');
    pending.dataset.dwNativeCartPending = '';
    pending.setAttribute('role', 'status');
    pending.setAttribute('aria-live', 'polite');

    var imageLink = createElement('a', 'dw-native-cart-pending__image-link');
    imageLink.href = product.url;
    imageLink.tabIndex = -1;
    if (product.image) {
      var image = createElement('img', 'dw-native-cart-pending__image');
      image.src = product.image;
      image.alt = '';
      image.width = 88;
      image.height = 88;
      imageLink.appendChild(image);
    }

    var details = createElement('div', 'dw-native-cart-pending__details');
    var title = createElement('a', 'dw-native-cart-pending__title', product.title);
    title.href = product.url;
    title.tabIndex = -1;
    details.appendChild(title);
    if (product.variantTitle) {
      details.appendChild(createElement('span', 'dw-native-cart-pending__variant', product.variantTitle));
    }

    var summary = createElement('div', 'dw-native-cart-pending__summary');
    summary.appendChild(createElement('span', 'dw-native-cart-pending__quantity', 'Qty ' + quantity));
    var price = createElement('span', 'dw-native-cart-pending__pricing');
    appendPendingPrice(price, product);
    summary.appendChild(price);
    details.appendChild(summary);

    var status = createElement('span', 'dw-native-cart-pending__status');
    status.appendChild(createElement('span', 'dw-native-cart-pending__spinner'));
    status.lastChild.setAttribute('aria-hidden', 'true');
    status.appendChild(createElement('span', '', 'Adding to cart...'));
    details.appendChild(status);

    pending.appendChild(imageLink);
    pending.appendChild(details);

    products.appendChild(pending);
    drawer.classList.remove('is-empty');
    drawer.classList.add('dw-native-cart--adding');
    updateCount(pendingCartCount + quantity);
    markCartStage('pending-rendered');
  }

  function handleProductAdding(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    pendingProductSubmit = true;
    setUpdating(true);
    showPendingAdd(form);
    document.body.classList.remove('modal-open-cart');

    // Let the add request start before drawer layout, focus trapping and
    // transitions run on the main thread.
    window.requestAnimationFrame(function () {
      document.dispatchEvent(new CustomEvent('cart:open'));
      window.requestAnimationFrame(function () {
        markCartStage('drawer-visible');
      });
    });
  }

  function handleClick(event) {
    var recommendationLink = event.target.closest('.dw-native-cart-card__image-link, .dw-native-cart-card__title');
    if (recommendationLink) {
      var recommendationCard = recommendationLink.closest('.dw-native-cart-card');
      var recommendationProduct = productMap[String(recommendationCard.dataset.productId)];
      var recommendationVariant = selectedVariant(recommendationCard, recommendationProduct);
      track('dw_native_cart_recommendation_click', recommendationProduct, recommendationCard.dataset.context, recommendationVariant);
      return;
    }

    var addButton = event.target.closest('.dw-native-cart-card__add');
    if (addButton) {
      event.preventDefault();
      addRecommendation(addButton);
      return;
    }

    var arrow = event.target.closest('[data-dw-native-cart-prev], [data-dw-native-cart-next]');
    if (arrow) {
      event.preventDefault();
      var container = arrow.closest('.dw-native-cart-recommendations');
      var trackElement = container.querySelector('[data-dw-native-cart-track]');
      var direction = arrow.hasAttribute('data-dw-native-cart-prev') ? -1 : 1;
      trackElement.scrollBy({ left: direction * trackElement.clientWidth, behavior: 'smooth' });
      return;
    }

    var removeLink = event.target.closest('#CartDrawer .cart__remove a');
    if (removeLink) {
      event.preventDefault();
      removeCartItem(removeLink);
      return;
    }

    if (event.target.closest('#CartDrawer .js-qty__adjust')) setUpdating(true);
  }

  function handleCartTrigger(event) {
    var trigger = event.target.closest('.js-drawer-open-cart');
    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    document.body.classList.remove('modal-open-cart');
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  function suppressCompetingCart() {
    if (document.body.classList.contains('modal-open-cart')) {
      document.body.classList.remove('modal-open-cart');
    }
  }

  function handleChange(event) {
    if (!event.target.matches('.dw-native-cart-card__variant')) return;
    updateCardVariant(event.target.closest('.dw-native-cart-card'));
  }

  function lockCartBackground() {
    if (window.dwOverlayScroll) { window.dwOverlayScroll.sync(); return; }
    if (pageScrollLock) return;
    var root = document.documentElement;
    var main = document.getElementById('MainContent');
    var scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
    pageScrollLock = {
      x: window.scrollX,
      y: window.scrollY,
      main: main,
      wasInert: main && main.hasAttribute('inert')
    };
    root.style.setProperty('--dw-cart-scroll-top', -pageScrollLock.y + 'px');
    root.style.setProperty('--dw-cart-scroll-padding',
      (parseFloat(window.getComputedStyle(document.body).paddingRight) + scrollbarWidth) + 'px');
    root.classList.add('dw-native-cart-open');
    if (main) main.setAttribute('inert', '');
  }

  function unlockCartBackground() {
    var root = document.documentElement;
    if (!pageScrollLock || root.classList.contains('js-drawer-open')) return;
    var saved = pageScrollLock;
    pageScrollLock = null;
    root.classList.remove('dw-native-cart-open');
    root.style.removeProperty('--dw-cart-scroll-top');
    root.style.removeProperty('--dw-cart-scroll-padding');
    if (saved.main && !saved.wasInert) saved.main.removeAttribute('inert');
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  }

  function handleCartOpen() {
    drawerHasOpened = true;
    if (!pendingProductSubmit && !awaitingCartMarkup()) {
      if (!lastCart || Date.now() - lastCartHydratedAt > 30000) {
        scheduleRefresh(0);
      } else {
        renderCart(lastCart, true);
      }
    }
    window.requestAnimationFrame(updateAllNavigation);
    window.setTimeout(updateAllNavigation, 460);
  }

  function init() {
    if (document.documentElement.dataset.dwNativeCartBound === 'true') return;
    document.documentElement.dataset.dwNativeCartBound = 'true';

    document.addEventListener('click', handleCartTrigger, true);
    document.addEventListener('ajaxProduct:adding', handleProductAdding);
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('cart:rendered', function (event) {
      clearPendingAdd(false);
      setUpdating(false);
      if (!renderCart(event.detail && event.detail.cart)) scheduleRefresh(20);
    });
    document.addEventListener('cart:updated', function (event) {
      if (!renderCart(event.detail && event.detail.cart)) scheduleRefresh(80);
    });
    document.addEventListener('ajaxProduct:added', function (event) {
      pendingProductSubmit = false;
      var detail = event.detail || {};
      var product = detail.product || {};
      var sections = detail.sections || product.sections || {};
      if (!sections['dw-native-cart-render']) scheduleRefresh(40);
    });
    document.addEventListener('ajaxProduct:error', function () {
      pendingProductSubmit = false;
      clearPendingAdd(true);
      setUpdating(awaitingCartMarkup());
    });
    document.addEventListener('cart:open', lockCartBackground, true);
    document.addEventListener('cart:open', handleCartOpen);
    document.addEventListener('drawerOpen.CartDrawer', handleCartOpen);
    document.addEventListener('drawerOpen.CartDrawer', lockCartBackground);
    window.addEventListener('resize', updateAllNavigation, { passive: true });

    if (window.MutationObserver) {
      new MutationObserver(unlockCartBackground).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
      new MutationObserver(suppressCompetingCart).observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    if (document.querySelector('#CartDrawer.drawer--is-open')) lockCartBackground();
    suppressCompetingCart();
    window.setTimeout(function () {
      if (!lastCart && (
        canLoadRecommendations(document.querySelector('[data-dw-native-cart-recommendations]')) ||
        canLoadRecommendations(document.querySelector('[data-dw-native-cart-empty-recommendations]'))
      )) scheduleRefresh(0);
    }, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

